/**
 * WebSocket 메시지 규격.
 *
 * 이 파일이 프로토콜의 **유일한 원본**이다. 화면 코드는 `web/shared/protocol.ts`가
 * 이 파일을 그대로 재수출하므로, 규격 변경은 여기 한 곳만 고치면 된다.
 *
 * - 텍스트 프레임: JSON (아래 판별 유니온)
 * - 바이너리 프레임: PCM 또는 Opus 오디오
 */

// ---------------------------------------------------------------------------
// 공통 값
// ---------------------------------------------------------------------------

/** 세션 상태기계 */
export type SessionState = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'stopping';

/** 자막 종류. input=원문(한국어), output=번역문(영어) */
export type CaptionKind = 'input' | 'output';

/** viewer 플랫폼 구분. 서버 로그와 operator `viewers` 집계에만 쓴다 */
export type ViewerClientKind = 'ios-safari' | 'android-chrome' | 'other';

/**
 * 한 viewer 연결에 나가는 바이너리 오디오 형식.
 * 두 형식 모두 앞 4바이트 `audioSeq`는 그대로 유지한다.
 */
export type ViewerAudioFormat = 'pcm' | 'opus';

/** server → operator 오류 코드 */
export type OperatorErrorCode =
  | 'gemini_connect_failed'
  | 'gemini_closed'
  | 'auth'
  | 'idle_stop'
  | 'operator_replaced'
  /** 인코더 고장으로 Opus 방송이 꺼졌다. 치명적이지 않다 */
  | 'opus_disabled'
  | 'internal';

/** server → viewer 오류 코드 */
export type ViewerErrorCode = 'session_ended';

// ---------------------------------------------------------------------------
// 오디오 바이너리 프레임 상수
// ---------------------------------------------------------------------------

/** operator → server 입력 오디오 샘플레이트 */
export const INPUT_SAMPLE_RATE = 16000;

/** 입력 프레임 길이 100ms */
export const INPUT_FRAME_MS = 100;

/** 100ms = 1600 samples */
export const INPUT_FRAME_SAMPLES = (INPUT_SAMPLE_RATE * INPUT_FRAME_MS) / 1000;

/** 100ms = 1600 samples = 3200 bytes (PCM16 mono LE, 헤더 없음) */
export const INPUT_FRAME_BYTES = INPUT_FRAME_SAMPLES * 2;

/** server → operator/viewer 출력 오디오 샘플레이트 (Gemini 응답 그대로) */
export const OUTPUT_SAMPLE_RATE = 24000;

/** 출력 바이너리 프레임 앞에 붙는 audioSeq(uint32 LE) 바이트 수 */
export const AUDIO_SEQ_PREFIX_BYTES = 4;

/**
 * viewer 백프레셔 임계값. 소켓 bufferedAmount가 이 값을 넘으면 오디오 프레임을
 * 건너뛰고(자막은 유지) 횟수를 로그한다.
 *
 * PCM 뷰어의 임계값은 이 값이다. Opus는 `VIEWER_BACKPRESSURE_SEC`를 쓴다.
 */
export const VIEWER_BACKPRESSURE_BYTES = 512 * 1024;

/**
 * 백프레셔를 **시간**으로 나타낸 값. 임계값은 "몇 초나 뒤처졌는가"다.
 *
 * 524,288바이트는 PCM에서 10.92초지만 Opus(실측 3,215 B/s)에서는 163초다 — 같은 바이트
 * 상수를 쓰면 압축 뷰어는 2분 40초 넘게 뒤처진 음성을 계속 듣게 된다.
 * PCM 공칭 송신율 48,000 B/s로 바이트 상수에서 유도하므로 PCM 뷰어의 임계값은 그대로다.
 */
export const VIEWER_BACKPRESSURE_SEC = VIEWER_BACKPRESSURE_BYTES / (OUTPUT_SAMPLE_RATE * 2);

// ---------------------------------------------------------------------------
// Opus 바이너리 프레임 규약
//
//   [audioSeq uint32 LE][0x4F 0x50 매직 2B][packetCount uint8][reserved uint8]
//   [len uint16 LE × N][opus packet × N]
//
// 앞 4바이트 audioSeq는 PCM 프레임과 같은 자리다. 형식 구분은 바이너리
// 스니핑이 아니라 `audioFormat` 제어 메시지가 하며, 매직 2바이트는 방어용이다.
// ---------------------------------------------------------------------------

/** 매직 'O' — audioSeq 바로 다음 바이트 */
export const OPUS_FRAME_MAGIC_0 = 0x4f;

/** 매직 'P' */
export const OPUS_FRAME_MAGIC_1 = 0x50;

/** seq 4 + 매직 2 + packetCount 1 + reserved 1. 길이표(2N)는 이 뒤에 붙는다 */
export const OPUS_FRAME_HEADER_BYTES = 8;

/** packetCount가 uint8이므로 한 프레임에 담을 수 있는 패킷 수의 상한 */
export const OPUS_MAX_PACKETS_PER_FRAME = 255;

/** Opus 패킷 하나가 담는 오디오 길이. 인코더·디코더 양쪽이 이 값으로 고정된다 */
export const OPUS_FRAME_MS = 20;

/**
 * viewer 접속 직후 재전송하는 최근 **확정** 자막 건수.
 * 서버 메모리 링버퍼에 seq당 완성 텍스트 1건씩 담고 `append:false`로 보낸다.
 */
export const VIEWER_CAPTION_REPLAY = 20;

// ---------------------------------------------------------------------------
// WebSocket close 코드 상수
// ---------------------------------------------------------------------------

/** operator 인증 실패 또는 2초 내 auth 미수신 */
export const WS_CLOSE_AUTH_FAILED = 4401;

/** 새 operator에게 세션을 넘기고 기존 연결을 닫을 때 */
export const WS_CLOSE_OPERATOR_REPLACED = 4409;

/** 업그레이드 완료 후 유효한 auth를 기다리는 시간 */
export const OPERATOR_AUTH_TIMEOUT_MS = 2000;

/** 클라이언트 keepalive 주기 */
export const PING_INTERVAL_MS = 15_000;

// ---------------------------------------------------------------------------
// operator → server
// ---------------------------------------------------------------------------

/** 접속 직후 첫 메시지. URL 쿼리에 토큰을 넣지 않기 위해 본문으로 전달한다. */
export interface OperatorAuthMessage {
  type: 'auth';
  token: string;
}

/** 통역 세션 열기. `targetLang`이 없으면 서버 설정 `TARGET_LANG`을 쓴다. */
export interface OperatorStartMessage {
  type: 'start';
  targetLang?: string;
}

/** 재접속 시 기존 세션에 재합류 (유예 60초) */
export interface OperatorResumeMessage {
  type: 'resume';
  sessionId: string;
}

/** 세션 닫기 */
export interface OperatorStopMessage {
  type: 'stop';
}

/** WS 선제 순환: 세션은 유지한 채 이 연결만 정상 종료 */
export interface OperatorCycleMessage {
  type: 'cycle';
}

/** keepalive */
export interface OperatorPingMessage {
  type: 'ping';
}

export type OperatorClientMessage =
  | OperatorAuthMessage
  | OperatorStartMessage
  | OperatorResumeMessage
  | OperatorStopMessage
  | OperatorCycleMessage
  | OperatorPingMessage;

export type OperatorClientMessageType = OperatorClientMessage['type'];

// ---------------------------------------------------------------------------
// server → operator
// ---------------------------------------------------------------------------

export interface StateMessage {
  type: 'state';
  state: SessionState;
  /** 세션이 없으면 null */
  sessionId: string | null;
  /** 이 상태로 진입한 시각 (ISO 8601) */
  since: string;
}

/**
 * 자막. 확정 전에는 같은 seq로 증분 조각만 보낸다(`final:false, append:true`).
 * 전문 재전송 금지.
 */
export interface CaptionMessage {
  type: 'caption';
  seq: number;
  /** ISO 8601 */
  ts: string;
  kind: CaptionKind;
  text: string;
  final: boolean;
  /** true면 같은 seq의 기존 텍스트 뒤에 이어 붙인다 */
  append: boolean;
}

export interface OperatorErrorMessage {
  type: 'error';
  code: OperatorErrorCode;
  message: string;
  fatal: boolean;
}

/** 30초 주기 사용량·비용 추정 */
export interface UsageMessage {
  type: 'usage';
  inputAudioSec: number;
  outputAudioSec: number;
  estCostUsd: number;
}

/**
 * 지금 음성을 켠 참가자가 어떤 형식으로 받고 있는지.
 * 운영자가 조작할 것은 없다. '자세히 보기'에 상태로만 보인다.
 */
export interface ViewerAudioSummary {
  /** 음성을 켜고 Opus를 받는 뷰어 수 */
  opus: number;
  /** 음성을 켜고 PCM을 받는 뷰어 수 */
  pcm: number;
  /** 지금 Opus 방송을 내보낼 수 있는가. `AUDIO_OPUS=off`이거나 자동 강등이면 false */
  enabled: boolean;
  /** 자동 강등으로 죽었는가. true면 이 서버가 다시 뜨기 전에는 되살아나지 않는다 */
  failed: boolean;
}

export interface ViewersMessage {
  type: 'viewers';
  count: number;
  /** 음성을 켠 참가자의 오디오 형식 요약 */
  audio: ViewerAudioSummary;
}

export interface PongMessage {
  type: 'pong';
}

export type ServerToOperatorMessage =
  | StateMessage
  | CaptionMessage
  | OperatorErrorMessage
  | UsageMessage
  | ViewersMessage
  | PongMessage;

export type ServerToOperatorMessageType = ServerToOperatorMessage['type'];

// ---------------------------------------------------------------------------
// viewer → server
// ---------------------------------------------------------------------------

/** viewer가 알린 오디오 해제 능력. `hello`의 선택 필드이며 없으면 pcm이 된다. */
export interface ViewerAudioCapabilities {
  /** WebCodecs `AudioDecoder`로 opus 48kHz 모노를 풀 수 있는가 */
  opus?: boolean;
}

export interface ViewerHelloMessage {
  type: 'hello';
  client: ViewerClientKind;
  /** 선택. 없으면 아무 능력도 없는 것으로 본다 */
  audio?: ViewerAudioCapabilities;
}

/**
 * 이 연결에 번역 음성을 보낼지. 연결 직후 기본값은 **보내지 않음**이고, 재접속하면
 * 다시 꺼진 상태로 시작하므로 화면이 켜 둔 상태를 연결마다 다시 알린다.
 */
export interface ViewerListenMessage {
  type: 'listen';
  on: boolean;
}

export interface ViewerPingMessage {
  type: 'ping';
}

export type ViewerClientMessage = ViewerHelloMessage | ViewerListenMessage | ViewerPingMessage;

export type ViewerClientMessageType = ViewerClientMessage['type'];

// ---------------------------------------------------------------------------
// server → viewer
// ---------------------------------------------------------------------------

export interface ViewerErrorMessage {
  type: 'error';
  code: ViewerErrorCode;
  message: string;
}

/**
 * 형식 경계 통지. **이 메시지 다음에 오는 바이너리 프레임부터** 해당 형식이다.
 * 한 WebSocket 연결 안에서는 순서가 보장되므로 경계가 정확하다.
 */
export interface AudioFormatMessage {
  type: 'audioFormat';
  format: ViewerAudioFormat;
}

export type ServerToViewerMessage =
  | StateMessage
  | CaptionMessage
  | ViewerErrorMessage
  | AudioFormatMessage
  | PongMessage;

export type ServerToViewerMessageType = ServerToViewerMessage['type'];

// ---------------------------------------------------------------------------
// 디코딩 헬퍼
// ---------------------------------------------------------------------------

/** 아직 판별되지 않은(신뢰할 수 없는) 수신 메시지 */
export interface UnknownMessage {
  type: string;
  [key: string]: unknown;
}

/**
 * 네트워크에서 온 텍스트 프레임을 파싱한다. JSON이 아니거나 `type`이 문자열이
 * 아니면 null. 반환값의 필드는 아직 검증되지 않았으므로 각 핸들러가 확인한다.
 */
export function decodeClientMessage(raw: string): UnknownMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const candidate = parsed as Record<string, unknown>;
  if (typeof candidate['type'] !== 'string') return null;
  return candidate as UnknownMessage;
}

/** viewer가 보고한 플랫폼 문자열을 알려진 값으로 좁힌다. */
export function normalizeViewerClient(value: unknown): ViewerClientKind {
  return value === 'ios-safari' || value === 'android-chrome' ? value : 'other';
}

/**
 * `hello.audio`에서 Opus 해제 능력을 읽는다.
 *
 * 필드가 없거나 객체가 아니거나 `opus`가 정확히 `true`가 아니면 **능력 없음**이다.
 * 구버전 클라이언트는 필드 자체가 없으므로 이 규칙만으로 pcm이 된다.
 */
export function readViewerOpusCapability(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return (value as Record<string, unknown>)['opus'] === true;
}
